import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { 
  Star, 
  Search, 
  RefreshCw,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Mail,
  Loader2,
  TrendingUp,
  MessageSquare,
  Headphones,
  Lightbulb,
  BarChart3,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

// StarDisplay component moved outside main component
const StarDisplay = ({ score, max = 5, size = 'sm' }) => {
  const starSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={cn(
            starSize,
            star <= score ? 'text-amber-400 fill-amber-400' : 'text-slate-300'
          )}
        />
      ))}
      <span className="ml-1 text-xs text-slate-500">{score}/{max}</span>
    </div>
  );
};

const SatisfactionSurveysPage = () => {
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statsPeriod, setStatsPeriod] = useState('all');
  
  // Detail modal
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Actions
  const [sendingReminder, setSendingReminder] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {};
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      
      const [surveysRes, statsRes] = await Promise.all([
        api.get('/satisfaction-surveys/', { params }),
        api.get('/satisfaction-surveys/stats', { params: { period: statsPeriod } })
      ]);
      
      setSurveys(surveysRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, statsPeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSendReminder = async (surveyId) => {
    setSendingReminder(surveyId);
    try {
      await api.post(`/satisfaction-surveys/${surveyId}/send-reminder`);
      toast.success('Rappel envoyé avec succès');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du rappel");
    } finally {
      setSendingReminder(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'pending': { label: 'En attente', className: 'bg-amber-100 text-amber-700', icon: Clock },
      'completed': { label: 'Complété', className: 'bg-green-100 text-green-700', icon: CheckCircle2 },
      'expired': { label: 'Expiré', className: 'bg-slate-100 text-slate-700', icon: AlertCircle },
      'skipped': { label: 'Ignoré', className: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const c = config[status] || config['pending'];
    const Icon = c.icon;
    return (
      <Badge className={cn("flex items-center gap-1", c.className)}>
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 16) return 'text-green-600';
    if (score >= 12) return 'text-amber-600';
    if (score >= 8) return 'text-orange-600';
    return 'text-red-600';
  };

  const filteredSurveys = surveys.filter(survey => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      survey.client_name?.toLowerCase().includes(term) ||
      survey.project_name?.toLowerCase().includes(term) ||
      survey.task_name?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      <main className={cn(
        "transition-all duration-300 px-4 md:px-6 pb-6",
        collapsed ? "md:ml-20" : "md:ml-64"
      )} style={{ paddingTop: "130px" }}>
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Enquêtes de satisfaction
            </h1>
            <p className="text-slate-500">Suivi des retours clients</p>
          </div>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
            Actualiser
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                  <BarChart3 className="w-3 h-3" />
                  Total
                </div>
                <p className="text-2xl font-bold">{stats.total_surveys}</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-green-500 text-xs mb-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Complétées
                </div>
                <p className="text-2xl font-bold text-green-600">{stats.completed_surveys}</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-amber-500 text-xs mb-1">
                  <TrendingUp className="w-3 h-3" />
                  Taux de réponse
                </div>
                <p className="text-2xl font-bold text-amber-600">{stats.response_rate}%</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-amber-600 text-xs mb-1">
                  <Star className="w-3 h-3 fill-amber-500" />
                  Note moyenne
                </div>
                <p className="text-2xl font-bold text-amber-600">{stats.avg_total_score}/20</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                  <Headphones className="w-3 h-3" />
                  Écoute
                </div>
                <p className="text-lg font-bold">{stats.avg_listening}/5</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                  <Lightbulb className="w-3 h-3" />
                  Créativité
                </div>
                <p className="text-lg font-bold">{stats.avg_creativity}/5</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Rechercher par client ou projet..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="w-full md:w-48">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="completed">Complétées</SelectItem>
                    <SelectItem value="skipped">Ignorées</SelectItem>
                    <SelectItem value="expired">Expirées</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full md:w-48">
                <Select value={statsPeriod} onValueChange={setStatsPeriod}>
                  <SelectTrigger>
                    <SelectValue placeholder="Période stats" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les périodes</SelectItem>
                    <SelectItem value="month">Ce mois</SelectItem>
                    <SelectItem value="quarter">Ce trimestre</SelectItem>
                    <SelectItem value="year">Cette année</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Surveys Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Enquêtes ({filteredSurveys.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : filteredSurveys.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Star className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Aucune enquête trouvée</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Projet / Tâche</TableHead>
                      <TableHead className="text-center">Note</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSurveys.map((survey) => (
                      <TableRow key={survey.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">
                          {survey.client_name || 'Client inconnu'}
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[200px]">
                            <p className="truncate font-medium">
                              {survey.project_name || survey.task_name || '-'}
                            </p>
                            {survey.task_name && survey.project_name && (
                              <p className="text-xs text-slate-500 truncate">
                                Tâche: {survey.task_name}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {survey.status === 'completed' && survey.total_score ? (
                            <span className={cn("text-xl font-bold", getScoreColor(survey.total_score))}>
                              {survey.total_score}/20
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(survey.status)}
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {survey.completed_at 
                            ? new Date(survey.completed_at).toLocaleDateString('fr-FR')
                            : new Date(survey.created_at).toLocaleDateString('fr-FR')
                          }
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {survey.status === 'completed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedSurvey(survey);
                                  setShowDetailModal(true);
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            {survey.status === 'pending' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSendReminder(survey.id)}
                                disabled={sendingReminder === survey.id}
                              >
                                {sendingReminder === survey.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Mail className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                Détail de l&apos;enquête
              </DialogTitle>
            </DialogHeader>
            {selectedSurvey && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Client</p>
                  <p className="font-medium">{selectedSurvey.client_name}</p>
                  <p className="text-sm text-slate-500 mt-2">Projet</p>
                  <p className="font-medium">{selectedSurvey.project_name || selectedSurvey.task_name}</p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Headphones className="w-4 h-4 text-slate-500" />
                      <span className="text-sm">L&apos;écoute</span>
                    </div>
                    <StarDisplay score={selectedSurvey.listening_score} />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-slate-500" />
                      <span className="text-sm">La créativité</span>
                    </div>
                    <StarDisplay score={selectedSurvey.creativity_score} />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-slate-500" />
                      <span className="text-sm">Le conseil</span>
                    </div>
                    <StarDisplay score={selectedSurvey.advice_score} />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-500" />
                      <span className="text-sm">Respect délai/budget</span>
                    </div>
                    <StarDisplay score={selectedSurvey.deadline_budget_score} />
                  </div>
                </div>
                
                <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-amber-800">Note finale</span>
                    <span className={cn("text-3xl font-bold", getScoreColor(selectedSurvey.total_score))}>
                      {selectedSurvey.total_score}/20
                    </span>
                  </div>
                </div>
                
                {selectedSurvey.comment && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Commentaire du client</p>
                    <p className="text-sm italic">&quot;{selectedSurvey.comment}&quot;</p>
                  </div>
                )}
                
                <p className="text-xs text-slate-500 text-center">
                  Complétée le {new Date(selectedSurvey.completed_at).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default SatisfactionSurveysPage;
